import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import { SignatureEdit } from 'webapp/components/common/Signature'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { Permit } from '@core/entities/permit/Permit'
import { useRejectPermit } from '@core/react-query/features/permits/permit'
import { ModalDialogContainer } from '@common/modal'
import { useToast } from '@core/toast'
import { TextEditor } from '@common/TextEditor'

interface ComponentType {
  permit: Permit
}

export const RejectPermitModal: React.FC<ComponentType> = ({ permit }) => {
  const rejectPermit = useRejectPermit()
  const { data: profile } = useGetProfile()
  const [reason, setReason] = useState<string>(null)
  const [error, setError] = useState('')
  const { hideModal } = useModal()
  const toast = useToast()

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.rejectPermit}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <p>{localizedStrings.rejectionReasonDescription}</p>

        <TextEditor
          value={reason}
          onChange={setReason}
          name='reason'
          placeholderChar={localizedStrings.enterReason}
          plainText
          sx={{ mb: 3 }}
        />

        <SignatureEdit readonly value={profile?.signatureUrl} />
        <ErrorMessage>{error}</ErrorMessage>
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <Button
          variant='contained'
          onClick={() =>
            rejectPermit.mutate(
              {
                permitId: permit.id,
                reason,
                approvalId: permit.approvalReviews?.nextApprovalId,
              },
              {
                onSuccess: () => {
                  hideModal()
                  setReason(null)
                  toast.success(localizedStrings.permitRejected)
                },
                onError: (e) => setError(e.message),
              },
            )
          }
          disabled={rejectPermit.isLoading || !reason}
          data-test='jsConfirmReject'
        >
          {localizedStrings.reject}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
